import { createElement, useEffect, useRef, useState, type ComponentType } from 'react';
import { Marker, Tooltip, useMap } from 'react-leaflet';
import { renderToStaticMarkup } from 'react-dom/server';
import { DivIcon } from 'leaflet';
import { type LucideProps, GraduationCap, Bus, ShoppingCart, Layers, Loader2 } from 'lucide-react';
import { cn } from '@/frontend/lib/utils';

export const NOMINATIM_BASE = '/api/nominatim';

export const TILE_URL = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png';
export const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>';

const GEOAPIFY_KEY = import.meta.env.VITE_GEOAPIFY_API_KEY as string;

export const createPoiIcon = (IconComponent: ComponentType<LucideProps>, color: string): DivIcon => {
  const svg = renderToStaticMarkup(
    createElement(IconComponent, { size: 13, color: 'white', strokeWidth: 2.5 })
  );
  return new DivIcon({
    html: `<div style="width:26px;height:26px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;box-shadow:0 2px 4px rgba(0,0,0,.3);border:2px solid white">${svg}</div>`,
    className: '',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    tooltipAnchor: [13, 0],
  });
};

export const pinIcon = new DivIcon({
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41" style="filter:drop-shadow(0 2px 3px rgba(0,0,0,0.35))"><path d="M12.5 0C5.6 0 0 5.6 0 12.5C0 22 12.5 41 12.5 41C12.5 41 25 22 25 12.5C25 5.6 19.4 0 12.5 0Z" fill="hsl(350,80%,59%)"/><circle cx="12.5" cy="12.5" r="4.5" fill="white" opacity="0.9"/></svg>`,
  className: '',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

export type LayerKey = 'universities' | 'transit' | 'supermarkets';

interface LayerConfigItem {
  key: LayerKey;
  label: string;
  icon: ComponentType<LucideProps>;
  activeClass: string;
  hoverClass: string;
  poiIcon: DivIcon;
}

export const LAYER_CONFIG: LayerConfigItem[] = [
  {
    key: 'universities',
    label: 'Universities',
    icon: GraduationCap,
    activeClass: 'bg-blue-500 text-white border-blue-500',
    hoverClass: 'hover:border-blue-400',
    poiIcon: createPoiIcon(GraduationCap, '#3b82f6'),
  },
  {
    key: 'transit',
    label: 'Transit',
    icon: Bus,
    activeClass: 'bg-green-500 text-white border-green-500',
    hoverClass: 'hover:border-green-400',
    poiIcon: createPoiIcon(Bus, '#22c55e'),
  },
  {
    key: 'supermarkets',
    label: 'Supermarkets',
    icon: ShoppingCart,
    activeClass: 'bg-orange-500 text-white border-orange-500',
    hoverClass: 'hover:border-orange-400',
    poiIcon: createPoiIcon(ShoppingCart, '#f97316'),
  },
];

export interface OverpassNode {
  id: number;
  lat: number;
  lon: number;
  tags?: Record<string, string>;
}

interface GeoapifyFeature {
  geometry: { coordinates: [number, number] };
  properties: { name?: string };
}

export type PoisState = Record<LayerKey, OverpassNode[]>;

export type LayerRadii = Record<LayerKey, number>;

export const DEFAULT_RADII: LayerRadii = { universities: 2000, transit: 600, supermarkets: 600 };

export async function fetchPois(
  center: [number, number],
  radii: LayerRadii = DEFAULT_RADII
): Promise<PoisState> {
  const [lat, lon] = center;

  const layers: { key: LayerKey; cats: string }[] = [
    { key: 'universities', cats: 'education.university,education.college' },
    { key: 'transit', cats: 'public_transport.bus,public_transport.train,public_transport.tram,public_transport.subway' },
    { key: 'supermarkets', cats: 'commercial.supermarket' },
  ];

  const results = await Promise.all(
    layers.map(({ key, cats }) =>
      fetch(
        `https://api.geoapify.com/v2/places?categories=${cats}&filter=circle:${lon},${lat},${radii[key]}&limit=30&apiKey=${GEOAPIFY_KEY}`
      )
        .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.json(); })
        .then((data: { features?: GeoapifyFeature[] }) => ({
          key,
          nodes: (data.features ?? []).map((f, i) => ({
            id: i,
            lat: f.geometry.coordinates[1],
            lon: f.geometry.coordinates[0],
            tags: { name: f.properties.name ?? '' },
          })),
        }))
        .catch(() => ({ key, nodes: [] as OverpassNode[] }))
    )
  );

  const result: PoisState = { universities: [], transit: [], supermarkets: [] };
  for (const { key, nodes } of results) result[key] = nodes;
  return result;
}

export function usePoiLayers(center: [number, number] | null, radii: LayerRadii = DEFAULT_RADII) {
  const [activeLayers, setActiveLayers] = useState<Record<LayerKey, boolean>>({
    universities: false,
    transit: false,
    supermarkets: false,
  });
  const [pois, setPois] = useState<PoisState>({ universities: [], transit: [], supermarkets: [] });
  const [poisLoading, setPoisLoading] = useState(false);
  const poisFetched = useRef(false);

  const toggleLayer = (layer: LayerKey) => {
    setActiveLayers(prev => {
      const next = { ...prev, [layer]: !prev[layer] };
      if (!poisFetched.current && Object.values(next).some(Boolean) && center) {
        poisFetched.current = true;
        setPoisLoading(true);
        fetchPois(center, radii)
          .then(setPois)
          .finally(() => setPoisLoading(false));
      }
      return next;
    });
  };

  return { activeLayers, pois, poisLoading, toggleLayer };
}

export function MapController({ interactive }: { interactive: boolean }) {
  const map = useMap();

  useEffect(() => {
    if (interactive) map.scrollWheelZoom.enable();
    else map.scrollWheelZoom.disable();
  }, [interactive, map]);

  useEffect(() => {
    const onFullscreenChange = () => setTimeout(() => map.invalidateSize(), 100);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, [map]);

  return null;
}

interface PoiLayerTogglesProps {
  activeLayers: Record<LayerKey, boolean>;
  poisLoading: boolean;
  toggleLayer: (layer: LayerKey) => void;
}

export function PoiLayerToggles({ activeLayers, poisLoading, toggleLayer }: PoiLayerTogglesProps) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!filtersOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
        setFiltersOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [filtersOpen]);

  return (
    <div ref={filtersRef} className="absolute bottom-2 left-3 z-[850]">

      {/* Desktop: pill buttons in a row */}
      <div className="hidden md:flex gap-1.5">
        {LAYER_CONFIG.map(({ key, label, icon: LayerIcon, activeClass, hoverClass }) => (
          <button
            key={key}
            onClick={() => toggleLayer(key)}
            disabled={poisLoading}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border shadow-sm transition-colors',
              activeLayers[key]
                ? activeClass
                : `bg-white/90 backdrop-blur-sm text-foreground border-border ${hoverClass}`
            )}
          >
            {poisLoading && activeLayers[key]
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <LayerIcon className="w-3.5 h-3.5" />}
            {label}
          </button>
        ))}
      </div>

      {/* Mobile: compact button + popover panel */}
      <div className="md:hidden">
        {filtersOpen && (
          <div className="absolute bottom-full mb-2 left-0 bg-white/95 backdrop-blur-sm border border-border rounded-xl shadow-lg p-2 flex flex-col gap-1.5 min-w-[160px]">
            {LAYER_CONFIG.map(({ key, label, icon: LayerIcon, activeClass }) => (
              <button
                key={key}
                onClick={() => toggleLayer(key)}
                disabled={poisLoading}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border w-full transition-colors',
                  activeLayers[key]
                    ? activeClass
                    : 'bg-background text-foreground border-border'
                )}
              >
                {poisLoading && activeLayers[key]
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <LayerIcon className="w-3.5 h-3.5" />}
                {label}
              </button>
            ))}
          </div>
        )}
        <button
          onClick={() => setFiltersOpen(prev => !prev)}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border shadow-sm transition-colors',
            Object.values(activeLayers).some(Boolean)
              ? 'bg-foreground text-background border-foreground'
              : 'bg-white/90 backdrop-blur-sm text-foreground border-border'
          )}
        >
          <Layers className="w-3.5 h-3.5" />
          Layers
          {Object.values(activeLayers).filter(Boolean).length > 0 && (
            <span className="bg-secondary text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold leading-none">
              {Object.values(activeLayers).filter(Boolean).length}
            </span>
          )}
        </button>
      </div>

    </div>
  );
}

interface PoiMarkersProps {
  activeLayers: Record<LayerKey, boolean>;
  pois: PoisState;
}

export function PoiMarkers({ activeLayers, pois }: PoiMarkersProps) {
  return (
    <>
      {LAYER_CONFIG.map(({ key, poiIcon }) =>
        activeLayers[key]
          ? pois[key].map((poi, i) => (
              <Marker
                key={`${key}-${i}`}
                position={[poi.lat, poi.lon]}
                icon={poiIcon}
              >
                <Tooltip>{poi.tags?.name ?? key.slice(0, -1)}</Tooltip>
              </Marker>
            ))
          : null
      )}
    </>
  );
}
